// Iter · Feb 2026 — Package Delivery Report tab.
//
// Purpose: Give a PM a per-brand delivery view of every package's items,
// grouped by Client → Brand → Package → Category, comparing done vs
// total (with a pending sub-count) side-by-side across months from the
// package start date until the last planned deliverable.
//
// This complements the existing "Summary" tab (which is a broader
// Client × Month pivot) by focusing on ONE package's full lifecycle,
// so PMs can see whether a package is on track without eyeballing the
// timeline.
//
// Data: fetches all packages for the workspace and then walks each
// package's month range (from package.start_date to end_date or 12
// months forward), fetching `content-items-enriched` for every month
// in parallel. Cells show `done/total · Np` and the pending pill hover
// lists the pending items + their open tasks (mirrors Summary tab).

import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import MultiSelect from "@/components/ui/multi-select";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { RefreshCw, Search, ChevronRight, ChevronDown, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import PackageHoverPreview from "@/components/packages/PackageHoverPreview";

const DONE_STATUSES = new Set(["published"]);
const EXCLUDED = new Set(["cancelled"]);

function monthStr(y, m) { return `${y}-${String(m).padStart(2, "0")}`; }
function todayMonth() {
  const d = new Date();
  return monthStr(d.getFullYear(), d.getMonth() + 1);
}
function shift(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return monthStr(d.getFullYear(), d.getMonth() + 1);
}
function monthLabel(ym) {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en", { month: "short", year: "2-digit" });
}
function cellTone(done, total) {
  if (total === 0) return "text-muted-foreground";
  if (done >= total) return "text-emerald-700 font-semibold";
  if (done === 0) return "text-rose-600 font-semibold";
  return "text-amber-700";
}

export default function PackageDeliveryReport({ wsId, currentMonth }) {
  const [rangeMode, setRangeMode] = useState("package_lifecycle"); // or "last_N"
  const [monthsBack, setMonthsBack] = useState("6");
  const [brandF, setBrandF] = useState([]);
  const [clientF, setClientF] = useState([]);
  const [packageF, setPackageF] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [raw, setRaw] = useState([]);        // flat items with __month
  const [packages, setPackages] = useState([]); // for lifecycle range
  const [sortKey, setSortKey] = useState("client");
  const [sortDir, setSortDir] = useState(1);
  const [expClients, setExpClients] = useState(() => new Set());
  const [expBrands, setExpBrands] = useState(() => new Set());
  const [expPkgs, setExpPkgs] = useState(() => new Set());
  const [exporting, setExporting] = useState(false);

  // Iter · Feb 2026 — Excel export. POSTs the current month range to
  // the backend which streams back a formatted .xlsx (green/amber/rose
  // conditional fills, frozen header, bold totals row).
  const exportExcel = async () => {
    if (!wsId) return;
    setExporting(true);
    try {
      const resp = await api.post(
        `/workspaces/${wsId}/content-calendar/package-delivery-export`,
        {
          months: monthList,
          client_ids: clientF.length ? clientF : null,
          brand_ids: brandF.length ? brandF : null,
        },
        { responseType: "blob" }
      );
      const blob = new Blob([resp.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `package-delivery-${monthList[0]}_${monthList[monthList.length - 1]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Exported");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Export failed"));
    } finally { setExporting(false); }
  };

  const toggle = (setter) => (k) => setter((prev) => {
    const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n;
  });

  // Compute the month list. In "package_lifecycle" mode we take the
  // MIN start_date and the MAX end_date (or current month + 3) across
  // all packages. Capped at 24 months so we don't accidentally spawn
  // 100 network calls.
  const monthList = useMemo(() => {
    if (rangeMode === "last_N") {
      const n = Number(monthsBack) || 6;
      const arr = [];
      for (let i = n - 1; i >= 0; i--) arr.push(shift(currentMonth, -i));
      return arr;
    }
    // lifecycle: min start → max end (or +3 months buffer)
    if (packages.length === 0) return [currentMonth];
    let minM = null, maxM = null;
    for (const p of packages) {
      const s = (p.start_date || "").slice(0, 7);
      const e = (p.end_date || "").slice(0, 7) || shift(currentMonth, 3);
      if (s && (!minM || s < minM)) minM = s;
      if (e && (!maxM || e > maxM)) maxM = e;
    }
    if (!minM) minM = currentMonth;
    if (!maxM) maxM = shift(currentMonth, 3);
    const arr = [];
    let cur = minM;
    let safety = 0;
    while (cur <= maxM && safety < 24) { arr.push(cur); cur = shift(cur, 1); safety += 1; }
    return arr;
  }, [rangeMode, monthsBack, currentMonth, packages]);

  const loadPackages = async () => {
    if (!wsId) return;
    try {
      const r = await api.get(`/workspaces/${wsId}/packages?status=active`);
      setPackages(r.data || []);
    } catch { setPackages([]); }
  };

  const load = async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const results = await Promise.all(
        monthList.map(async (m) => {
          try {
            const r = await api.get(`/workspaces/${wsId}/content-items-enriched?month=${m}&limit=5000`);
            return (r.data?.rows || r.data || []).map((it) => ({ ...it, __month: m }));
          } catch { return []; }
        })
      );
      setRaw(results.flat());
    } catch (e) {
      toast.error(apiErrorMessage(e, "Load failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPackages(); /* eslint-disable-next-line */ }, [wsId]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, rangeMode, monthsBack, currentMonth, packages]);

  // Filter options derived from raw.
  const { clientOptions, brandOptions, packageOptions } = useMemo(() => {
    const cs = new Map(), bs = new Map(), ps = new Map();
    for (const it of raw) {
      const ck = it.client_id || it.client_name || "_none";
      cs.set(ck, { value: ck, label: it.client_name || "— No client —" });
      const bk = it.brand_id || it.brand_name || "_none";
      bs.set(bk, { value: bk, label: it.brand_name || "—" });
      const pk = it.package_id || it.package_name || "_none";
      ps.set(pk, { value: pk, label: it.package_name || "— No package —" });
    }
    return {
      clientOptions: [...cs.values()].sort((a, b) => a.label.localeCompare(b.label)),
      brandOptions: [...bs.values()].sort((a, b) => a.label.localeCompare(b.label)),
      packageOptions: [...ps.values()].sort((a, b) => a.label.localeCompare(b.label)),
    };
  }, [raw]);

  // Hierarchical pivot: Client → Brand → Package → Category × month.
  const pivot = useMemo(() => {
    const qq = q.trim().toLowerCase();
    const clients = new Map();
    const ensure = (m, k, factory) => { if (!m.has(k)) m.set(k, factory()); return m.get(k); };
    const bumpCell = (obj, m, isDone) => {
      const c = obj[m] || { done: 0, total: 0 };
      c.total += 1;
      if (isDone) c.done += 1;
      obj[m] = c;
    };
    for (const it of raw) {
      if (EXCLUDED.has(it.status)) continue;
      if (brandF.length && !brandF.includes(it.brand_id)) continue;
      if (clientF.length && !clientF.includes(it.client_id || it.client_name || "_none")) continue;
      if (packageF.length && !packageF.includes(it.package_id || it.package_name || "_none")) continue;
      if (qq) {
        const hay = `${it.client_name || ""} ${it.brand_name || ""} ${it.package_name || ""} ${it.category || ""} ${it.name || ""}`.toLowerCase();
        if (!hay.includes(qq)) continue;
      }
      const ck = it.client_id || it.client_name || "_none";
      const bk = it.brand_id || it.brand_name || "_none";
      const pk = it.package_id || it.package_name || "_none";
      const catk = it.category || "Uncategorised";
      const isDone = DONE_STATUSES.has(it.status);

      const c = ensure(clients, ck, () => ({ key: ck, name: it.client_name || "— No client —", brands: new Map(), months: {} }));
      bumpCell(c.months, it.__month, isDone);
      const b = ensure(c.brands, bk, () => ({ key: bk, name: it.brand_name || "—", pkgs: new Map(), months: {} }));
      bumpCell(b.months, it.__month, isDone);
      const p = ensure(b.pkgs, pk, () => ({ key: pk, name: it.package_name || "— No package —", cats: new Map(), months: {} }));
      bumpCell(p.months, it.__month, isDone);
      const cat = ensure(p.cats, catk, () => ({ key: catk, name: catk, color: it.activity_type_color, items: [], months: {} }));
      cat.items.push(it);
      if (!cat.color && it.activity_type_color) cat.color = it.activity_type_color;
      bumpCell(cat.months, it.__month, isDone);
    }
    const clientArr = [...clients.values()].map((c) => ({
      ...c,
      brands: [...c.brands.values()].map((b) => ({
        ...b,
        pkgs: [...b.pkgs.values()].map((p) => ({
          ...p,
          cats: [...p.cats.values()].sort((a, b2) => a.name.localeCompare(b2.name)),
        })).sort((a, b2) => a.name.localeCompare(b2.name)),
      })).sort((a, b2) => a.name.localeCompare(b2.name)),
    }));
    // sortable at client level
    const key = (c) => sortKey === "client" ? c.name : sortKey === "delivered"
      ? Object.values(c.months).reduce((n, x) => n + x.done, 0) / Math.max(1, Object.values(c.months).reduce((n, x) => n + x.total, 0))
      : c.name;
    clientArr.sort((a, b) => {
      const ka = key(a), kb = key(b);
      if (typeof ka === "string") return ka.localeCompare(kb) * sortDir;
      return (ka - kb) * sortDir;
    });
    return clientArr;
  }, [raw, brandF, clientF, packageF, q, sortKey, sortDir]);

  const setSort = (k) => {
    if (sortKey === k) setSortDir((d) => -d); else { setSortKey(k); setSortDir(1); }
  };

  const grandTotals = useMemo(() => {
    let done = 0, total = 0;
    for (const c of pivot) for (const bm of Object.values(c.months)) {
      done += bm.done; total += bm.total;
    }
    return { done, total };
  }, [pivot]);

  // Pending hover breakdown (reused from Summary tab semantics).
  const pendingList = (items, m) => {
    return (items || [])
      .filter((it) => it.__month === m && it.status !== "published" && it.status !== "cancelled")
      .sort((a, b) => (a.planned_date || "").localeCompare(b.planned_date || ""));
  };

  const renderCell = (cellObj, m, items, testid) => {
    const cell = cellObj[m];
    if (!cell) {
      return <td key={m} className="text-center px-2 py-1 text-muted-foreground/40" data-testid={testid}>—</td>;
    }
    const pending = cell.total - cell.done;
    const scoped = items ? pendingList(items, m) : [];
    return (
      <td key={m} className={`text-center px-2 py-1 font-mono ${cellTone(cell.done, cell.total)}`}
          title={`Delivered ${cell.done} of ${cell.total} · ${pending} pending`}
          data-testid={testid}>
        {cell.done}/{cell.total}
        {pending > 0 && (
          items ? (
            <HoverCard openDelay={120} closeDelay={80}>
              <HoverCardTrigger asChild>
                <span className="block text-[9px] text-rose-600 font-normal cursor-help underline decoration-dotted underline-offset-2"
                      data-testid={`${testid}-pending`}>
                  {pending} pending
                </span>
              </HoverCardTrigger>
              <HoverCardContent side="right" align="start" className="w-80 p-0 rounded-none max-h-72 overflow-auto">
                <div className="px-3 py-2 border-b border-border bg-muted/40">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                    {pending} pending in {monthLabel(m)}
                  </div>
                </div>
                <ul className="divide-y divide-border">
                  {scoped.map((b) => {
                    const tasks = (b.tasks || []).filter((t) => t.status !== "done" && t.status !== "cancelled");
                    return (
                      <li key={b.id} className="px-3 py-2 text-[11px]">
                        <div className="font-medium truncate flex items-center gap-1.5">
                          <span className="font-mono text-[9px] text-muted-foreground">{(b.planned_date || "").slice(5)}</span>
                          <span className="truncate">{b.name}</span>
                        </div>
                        <div className="text-[9px] uppercase tracking-widest text-amber-700 mt-0.5">{(b.status || "").replaceAll("_", " ")}</div>
                        {tasks.length > 0 && (
                          <ul className="mt-1 space-y-0.5">
                            {tasks.slice(0, 5).map((t) => (
                              <li key={t.id} className="text-[10px] text-muted-foreground flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                                <span className="truncate flex-1">{t.title}</span>
                                {t.assignee_name && <span className="text-[9px] shrink-0">· {t.assignee_name}</span>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </HoverCardContent>
            </HoverCard>
          ) : (
            <span className="block text-[9px] text-rose-600">{pending} pending</span>
          )
        )}
      </td>
    );
  };

  return (
    <div className="space-y-3" data-testid="cc-pkg-delivery">
      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap border border-border bg-muted/20 px-3 py-2">
        <div className="flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Range</span>
          <Select value={rangeMode} onValueChange={setRangeMode}>
            <SelectTrigger className="h-7 w-[190px] rounded-none text-[11px] px-2" data-testid="cc-pkg-range-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="package_lifecycle">Full package lifecycle</SelectItem>
              <SelectItem value="last_N">Last N months</SelectItem>
            </SelectContent>
          </Select>
          {rangeMode === "last_N" && (
            <Select value={monthsBack} onValueChange={setMonthsBack}>
              <SelectTrigger className="h-7 w-[110px] rounded-none text-[11px] px-2" data-testid="cc-pkg-months-back">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["3", "6", "12", "24"].map((n) => <SelectItem key={n} value={n}>Last {n}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
        <MultiSelect options={clientOptions} value={clientF} onChange={setClientF}
                     allLabel="All clients" placeholder="Client" width="w-44" testid="cc-pkg-client-filter" />
        <MultiSelect options={brandOptions} value={brandF} onChange={setBrandF}
                     allLabel="All brands" placeholder="Brand" width="w-44" testid="cc-pkg-brand-filter" />
        <MultiSelect options={packageOptions} value={packageF} onChange={setPackageF}
                     allLabel="All packages" placeholder="Package" width="w-44" testid="cc-pkg-package-filter" />
        <div className="flex items-center gap-1">
          <Search className="w-3.5 h-3.5 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search…"
                 className="h-7 w-40 text-xs px-2 border border-border rounded-none bg-white"
                 data-testid="cc-pkg-search" />
        </div>
        <Button variant="ghost" size="sm" className="h-7 rounded-none text-[11px]"
                onClick={load} data-testid="cc-pkg-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button variant="outline" size="sm" className="h-7 rounded-none text-[11px]"
                onClick={exportExcel} disabled={exporting || loading || monthList.length === 0}
                data-testid="cc-pkg-export">
          <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> {exporting ? "Exporting…" : "Export Excel"}
        </Button>
        <span className="ml-auto text-[10px] text-muted-foreground" data-testid="cc-pkg-count">
          {grandTotals.done} of {grandTotals.total} delivered
          ({grandTotals.total ? Math.round((grandTotals.done / grandTotals.total) * 100) : 0}%)
        </span>
      </div>

      {/* Pivot */}
      <div className="border border-border overflow-x-auto">
        <table className="min-w-full text-[11px] border-collapse" data-testid="cc-pkg-table">
          <thead className="bg-muted/40 sticky top-0 z-[5]">
            <tr>
              <th className="text-left px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold w-[280px] sticky left-0 z-[6] bg-muted/60">
                <button onClick={() => setSort("client")} className="hover:text-foreground" data-testid="cc-pkg-sort-client">
                  Client / Brand / Package / Category {sortKey === "client" ? (sortDir > 0 ? "▲" : "▼") : ""}
                </button>
              </th>
              {monthList.map((m) => (
                <th key={m} className="text-center px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold whitespace-nowrap">
                  {monthLabel(m)}
                </th>
              ))}
              <th className="text-center px-2 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold whitespace-nowrap bg-muted/60">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={monthList.length + 2} className="text-center px-3 py-6 text-muted-foreground">Loading {monthList.length} months…</td></tr>
            )}
            {!loading && pivot.length === 0 && (
              <tr><td colSpan={monthList.length + 2} className="text-center px-3 py-6 text-muted-foreground">No deliverables in this range.</td></tr>
            )}
            {!loading && pivot.map((c) => {
              const cExpanded = expClients.has(c.key);
              let cDone = 0, cTotal = 0;
              for (const cell of Object.values(c.months)) { cDone += cell.done; cTotal += cell.total; }
              return (
                <React.Fragment key={c.key}>
                  <tr className="border-t border-border/60 hover:bg-muted/25"
                      data-testid={`cc-pkg-client-${c.key}`}>
                    <td className="px-3 py-1 sticky left-0 z-[4] bg-background border-r border-border">
                      <button className="flex items-center gap-1.5 text-left font-semibold text-indigo-700"
                              onClick={() => toggle(setExpClients)(c.key)}
                              data-testid={`cc-pkg-client-toggle-${c.key}`}>
                        {cExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        <span className="truncate">{c.name}</span>
                        <span className="text-[9px] font-normal text-muted-foreground">
                          {c.brands.length} brand{c.brands.length === 1 ? "" : "s"}
                        </span>
                      </button>
                    </td>
                    {monthList.map((m) => renderCell(c.months, m, null, `cc-pkg-cell-c-${c.key}-${m}`))}
                    <td className={`text-center px-2 py-1 font-mono bg-muted/40 ${cellTone(cDone, cTotal)}`}
                        data-testid={`cc-pkg-total-c-${c.key}`}>
                      {cDone}/{cTotal}
                    </td>
                  </tr>
                  {cExpanded && c.brands.map((b) => {
                    const bKey = `${c.key}/${b.key}`;
                    const bExpanded = expBrands.has(bKey);
                    let bDone = 0, bTotal = 0;
                    for (const cell of Object.values(b.months)) { bDone += cell.done; bTotal += cell.total; }
                    return (
                      <React.Fragment key={bKey}>
                        <tr className="bg-muted/10 border-t border-border/40 hover:bg-muted/25"
                            data-testid={`cc-pkg-brand-${bKey}`}>
                          <td className="px-3 pl-8 py-1 sticky left-0 z-[4] bg-muted/10 border-r border-border">
                            <button className="flex items-center gap-1.5 text-left"
                                    onClick={() => toggle(setExpBrands)(bKey)}
                                    data-testid={`cc-pkg-brand-toggle-${bKey}`}>
                              {bExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              <span className="text-[11px] font-medium truncate">{b.name}</span>
                              <span className="text-[9px] text-muted-foreground">
                                {b.pkgs.length} pkg{b.pkgs.length === 1 ? "" : "s"}
                              </span>
                            </button>
                          </td>
                          {monthList.map((m) => renderCell(b.months, m, null, `cc-pkg-cell-b-${bKey}-${m}`))}
                          <td className={`text-center px-2 py-1 font-mono bg-muted/30 ${cellTone(bDone, bTotal)}`}>
                            {bDone}/{bTotal}
                          </td>
                        </tr>
                        {bExpanded && b.pkgs.map((p) => {
                          const pKey = `${bKey}/${p.key}`;
                          const pExpanded = expPkgs.has(pKey);
                          let pDone = 0, pTotal = 0;
                          for (const cell of Object.values(p.months)) { pDone += cell.done; pTotal += cell.total; }
                          const pkgItems = p.cats.flatMap((cat) => cat.items);
                          return (
                            <React.Fragment key={pKey}>
                              <tr className="bg-muted/5 border-t border-border/30 hover:bg-muted/20"
                                  data-testid={`cc-pkg-pkg-${pKey}`}>
                                <td className="px-3 pl-14 py-1 sticky left-0 z-[4] bg-muted/5 border-r border-border">
                                  <button className="flex items-center gap-1.5 text-left"
                                          onClick={() => toggle(setExpPkgs)(pKey)}
                                          data-testid={`cc-pkg-pkg-toggle-${pKey}`}>
                                    {pExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                    <PackageHoverPreview pid={pKey && pKey !== "_none" ? pKey : null} disabled={!pKey || pKey === "_none"}>
                                      <span className="chip text-[10px] bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30 truncate cursor-help">{p.name}</span>
                                    </PackageHoverPreview>
                                    <span className="text-[9px] text-muted-foreground">{p.cats.length} cat</span>
                                  </button>
                                </td>
                                {monthList.map((m) => renderCell(p.months, m, pkgItems, `cc-pkg-cell-p-${pKey}-${m}`))}
                                <td className={`text-center px-2 py-1 font-mono bg-muted/20 ${cellTone(pDone, pTotal)}`}>
                                  {pDone}/{pTotal}
                                </td>
                              </tr>
                              {pExpanded && p.cats.map((cat) => {
                                let cDone2 = 0, cTotal2 = 0;
                                for (const cell of Object.values(cat.months)) { cDone2 += cell.done; cTotal2 += cell.total; }
                                return (
                                  <tr key={cat.key}
                                      className="border-t border-border/20 hover:bg-muted/15"
                                      data-testid={`cc-pkg-cat-${pKey}-${cat.key}`}>
                                    <td className="px-3 pl-20 py-1 sticky left-0 z-[4] bg-background border-r border-border">
                                      <div className="flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full" style={{ background: cat.color || "#8b5cf6" }} />
                                        <span className="text-[10.5px] truncate">{cat.name}</span>
                                        <span className="text-[9px] text-muted-foreground">{cat.items.length}</span>
                                      </div>
                                    </td>
                                    {monthList.map((m) => renderCell(cat.months, m, cat.items, `cc-pkg-cell-cat-${pKey}-${cat.key}-${m}`))}
                                    <td className={`text-center px-2 py-1 font-mono bg-muted/10 ${cellTone(cDone2, cTotal2)}`}>
                                      {cDone2}/{cTotal2}
                                    </td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-muted-foreground">
        Delivered = <b>published</b>. Cancelled items excluded from totals. Rows expandable to Brand → Package → Category.
      </div>
    </div>
  );
}
